import isEmptyValue from 'components/aha_editor_field/isEmptyValue';
import { AhaEditorUtils } from '@aha-app/aha-editor-v2';

const checkEditorEmpty = input => {
  if (input.value === '') return input;

  if (input.closest('.aha-editor-field-v2')) {
    if (AhaEditorUtils.Editor.isEmpty(JSON.parse(input.value))) {
      input = {
        value: '',
      };
    }
  } else {
    if (isEmptyValue(JSON.parse(input.value))) {
      input = {
        value: '',
      };
    }
  }

  return input;
};

// For each rule, make sure the custom_field_definition_id and array_value
// IDs are strings. This is only really necessary for testing because some of
// the fixture IDs are short enough to be returned as integers.
// https://github.com/aha-app/aha-app/blob/master/lib/gem_ext/active_support/numeric.rb#L9
export const stringifyIds = elements => {
  elements.forEach(element => {
    element.formDefinitionRules.forEach(rule => {
      if (rule.customFieldDefinitionId) {
        rule.customFieldDefinitionId = rule.customFieldDefinitionId.toString();
      }
      rule.arrayValue = rule.arrayValue.map(el => el.toString());
    });
  });
};

// Recursively evaluate all possible remaining paths through the form to
// see the longest possible path, which we use to show progress. This means
// progress can advance non-linearly as making a selection on one step
// prunes the list of possible remaining paths.
export const possibleRemainingSteps = (element, elements, visitedIds) => {
  const formRules = element.formDefinitionRules.filter(
    rule =>
      rule.ruleType === 'form' &&
      !visitedIds.includes(rule.descendantFormDefinitionElementId)
  );
  if (formRules.length > 0) {
    const possibleRemaining = formRules.map(rule =>
      possibleRemainingSteps(
        elements.find(el => rule.descendantFormDefinitionElementId === el.id),
        elements,
        [...visitedIds, element.id]
      )
    );
    return 1 + Math.max(...possibleRemaining);
  } else {
    return 1;
  }
};

// Bulk-enable or disable all of the fields -- we disable when you go to the
// review screen, and enable if you click 'back' from there.
export const setInputState = (formId, disabled) => {
  ['input', 'select'].forEach(elementType => {
    Array.from(document.querySelectorAll(`${formId} ${elementType}`)).forEach(
      input => {
        if (
          input.type !== 'submit' &&
          ![
            'ideas_idea_user_email',
            'ideas_portal_user_submit_form_password',
          ].includes(input.id)
        ) {
          input.disabled = disabled;
        }
      }
    );
  });

  // Disable the selectro-v2 dropdowns
  Array.from(
    document.querySelectorAll('.SelectroV2__mount-container [data-selectro]')
  ).forEach(selectroElement => {
    selectroElement.selectro.state.disabled = disabled;
  });

  Array.from(document.querySelectorAll(`${formId} .aha-editor-field`)).forEach(
    editor => {
      if (disabled) {
        editor.classList.add('aha-editor-field--disabled');
      } else {
        editor.classList.remove('aha-editor-field--disabled');
      }
    }
  );
};

// Append an error help block to a field to show a validation message.
export const setHelpBlock = (container, translations) => {
  const controls = container.querySelector(':scope .controls');
  controls.classList.add('field-with-errors');
  let block = controls.querySelector(':scope .help-inline');
  if (!block) {
    block = document.createElement('div');
    block.classList.add('help-inline');
    block.classList.add('below');
    controls.appendChild(block);
  }
  block.innerText = translations.fieldRequired;
};

// Clear any help block currently shown on the field.
const clearHelpBlock = container => {
  const controls = container.querySelector(':scope .controls');
  controls.classList.remove('field-with-errors');
  if (controls.querySelector(':scope .help-inline')) {
    controls.querySelector(':scope .help-inline').remove();
  }
};

// For a given section, iterate over each of its fields. If all of them
// are valid (i.e. required ones are populated) then return true, else
// append help blocks to any missing fields and return false.
export const validateSectionFields = (section, translations) => {
  let allFieldsValid = true;

  section.customFieldDefinitions
    .filter(def => def.required || def.static)
    .forEach(definition => {
      let inputWrapper;
      let input;

      if (definition.id === 'name') {
        inputWrapper = document.querySelector('.new-idea-fields');
        input = inputWrapper.querySelector(':scope input#ideas_idea_name');
      } else if (definition.id === 'idea_categories') {
        input = document.querySelector('#ideas_idea_idea_category_ids');
        inputWrapper = input?.parentElement?.parentElement;
      } else if (definition.id === 'description') {
        input = document.querySelector(
          '[name="ideas_idea[description_attributes][editor_content]"]'
        );
        inputWrapper = input.closest('.note');
        input = checkEditorEmpty(input);
      } else {
        inputWrapper = document.querySelector(
          `#ideas_idea_custom_field_${definition.key}`
        );

        input = inputForCustomFieldDefinition(definition, inputWrapper);
      }

      if (inputWrapper) {
        if (input?.value === undefined || input?.value === '') {
          setHelpBlock(inputWrapper, translations);
          allFieldsValid = false;
        } else {
          clearHelpBlock(inputWrapper);
        }
      }
    });

  // Auto-focus on first field with errors
  const firstErrorField = document.querySelector(
    'form.new_ideas_idea .controls.field-with-errors'
  );
  if (firstErrorField) {
    const firstErrorFieldInput = firstErrorField.querySelector(
      ':scope input, :scope [data-slate-editor], :scope select'
    );
    if (firstErrorFieldInput) {
      firstErrorFieldInput.focus();
    } else {
      firstErrorField.focus();
    }
  }

  return allFieldsValid;
};

export const inputForCustomFieldDefinition = (definition, inputWrapper) => {
  let input;

  switch (definition.type) {
    case 'CustomFieldDefinitions::TextField':
    case 'CustomFieldDefinitions::NumberField':
    case 'CustomFieldDefinitions::DateField':
    case 'CustomFieldDefinitions::UrlField':
    case 'CustomFieldDefinitions::SelectConstant':
    case 'CustomFieldDefinitions::SelectEditable':
      input =
        inputWrapper.querySelector(':scope .input-xxlarge') ||
        inputWrapper.querySelector(':scope .input-small');
      break;

    case 'CustomFieldDefinitions::SelectMultipleConstant':
    case 'CustomFieldDefinitions::SelectMultipleEditable':
    case 'CustomFieldDefinitions::LinkMany':
    case 'CustomFieldDefinitions::LinkOne': {
      const selectedInputs = Array.from(
        inputWrapper.querySelectorAll(
          ':scope .SelectroV2__mount-container input.SelectroV2__hidden-selected-input'
        )
      );
      if (selectedInputs.length > 0) {
        input = {
          value:
            selectedInputs.length > 0
              ? selectedInputs.map(input => input.value)
              : undefined,
        };
      } else {
        input = null;
      }
      break;
    }

    case 'CustomFieldDefinitions::AttachmentField':
      input = inputWrapper.querySelector(':scope .attachment input');
      break;

    case 'CustomFieldDefinitions::NoteField':
      input = inputWrapper.querySelector(':scope input');
      input = checkEditorEmpty(input);
  }

  return input;
};

// Toggle the email/password box which is shown on the review step if the user
// is not yet logged in.
export const toggleEmailPassword = visible => {
  [
    '#email-group',
    '#password-group',
    '#consent-group',
    '.aha-privacy-policy-one-line',
  ].forEach(selector => {
    const $element = document.querySelector(selector);
    if ($element) {
      if (visible) {
        $element.classList.remove('hidden');
      } else {
        $element.classList.add('hidden');
      }
    }
  });
};

// Show the review screen -- unhide all visited sections and show in a disabled
// state so the user can see what they entered during the flow.
export const showReviewScreen = (
  visitedSections,
  formId,
  translations,
  $header,
  $saveButton,
  $saveAndAddProxyVoteButton
) => {
  document
    .querySelector('.new-idea-fields .control-group')
    .classList.remove('hidden');
  visitedSections.forEach(section => {
    document.querySelector(`#section-${section.id}`).classList.remove('hidden');
  });
  toggleEmailPassword(true);
  setInputState(formId, true);

  $header.innerText = translations.reviewIdea;
  $header.scrollIntoView();
  let value = translations.addIdea;
  if ($saveButton.dataset.titleize === undefined) {
    value = value.toUpperCase();
  }

  $saveButton.value = value;

  if ($saveAndAddProxyVoteButton) {
    $saveButton.classList.replace('btn-primary', 'btn-outline');
    $saveAndAddProxyVoteButton.value =
      $saveAndAddProxyVoteButton.dataset.titleize === undefined
        ? translations.saveAndAddProxyVote.toUpperCase()
        : translations.saveAndAddProxyVote;

    $saveAndAddProxyVoteButton.classList.remove('hidden');
  }
};
